import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, GraduationCap } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/Card";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface TrainingRecord {
  id: number;
  volunteer_id: number;
  training_id: number | null;
  completion_date: string | null;
  notes: string | null;
  volunteer_name?: string;
  training_name?: string;
}

interface Volunteer {
  id: number;
  first_name: string;
  last_name: string;
  is_active: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function VolunteerTraining() {
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [formVolunteerId, setFormVolunteerId] = useState("");
  const [formDate, setFormDate] = useState("");
  const [formNotes, setFormNotes] = useState("");

  const { data: records, isLoading } = useQuery<TrainingRecord[]>({
    queryKey: ["volunteer-training"],
    queryFn: async () => {
      const res = await api.get<{ data: TrainingRecord[] }>("/volunteer-training");
      return res.data;
    },
  });

  const { data: volunteers } = useQuery<Volunteer[]>({
    queryKey: ["volunteers", "active"],
    queryFn: async () => {
      const res = await api.get<{ data: Volunteer[] }>("/volunteers", {
        params: { activeOnly: "true" },
      });
      return res.data;
    },
  });

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post("/volunteer-training", data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["volunteer-training"] });
      resetForm();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/volunteer-training/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["volunteer-training"] });
    },
  });

  function resetForm() {
    setShowForm(false);
    setFormVolunteerId("");
    setFormDate("");
    setFormNotes("");
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    createMutation.mutate({
      volunteerId: parseInt(formVolunteerId, 10),
      completionDate: formDate || undefined,
      notes: formNotes || undefined,
    });
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-foreground">
            Training Records
          </h2>
          <p className="text-sm text-muted-foreground">
            Track volunteer training completions
          </p>
        </div>
        <Button
          size="sm"
          className="gap-2"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          <Plus className="h-4 w-4" />
          Add Record
        </Button>
      </div>

      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">New Training Record</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="space-y-1.5">
                  <label className="text-sm font-medium leading-none text-foreground">
                    Volunteer *
                  </label>
                  <select
                    value={formVolunteerId}
                    onChange={(e) => setFormVolunteerId(e.target.value)}
                    required
                    className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    <option value="">Select volunteer...</option>
                    {(volunteers ?? []).map((v) => (
                      <option key={v.id} value={v.id}>
                        {v.last_name}, {v.first_name}
                      </option>
                    ))}
                  </select>
                </div>
                <Input
                  label="Completion Date"
                  type="date"
                  value={formDate}
                  onChange={(e) => setFormDate(e.target.value)}
                />
                <Input
                  label="Notes"
                  value={formNotes}
                  onChange={(e) => setFormNotes(e.target.value)}
                  placeholder="Training details..."
                />
              </div>

              {createMutation.isError && (
                <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
                  {(createMutation.error as Error)?.message ??
                    "An error occurred"}
                </div>
              )}

              <div className="flex gap-3">
                <Button type="submit" loading={createMutation.isPending}>
                  Add Record
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {isLoading && (
        <div className="h-64 animate-pulse rounded-lg border bg-muted" />
      )}

      {!isLoading && (records ?? []).length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <GraduationCap className="mx-auto h-10 w-10 text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              No training records yet.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading && (records ?? []).length > 0 && (
        <Card>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b bg-muted/50">
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                    Volunteer
                  </th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                    Training
                  </th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                    Completion Date
                  </th>
                  <th className="px-4 py-2.5 text-left font-medium text-muted-foreground">
                    Notes
                  </th>
                  <th className="px-4 py-2.5 text-right font-medium text-muted-foreground">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {(records ?? []).map((record) => (
                  <tr
                    key={record.id}
                    className="border-b last:border-0 hover:bg-muted/30"
                  >
                    <td className="px-4 py-3 font-medium">
                      {record.volunteer_name ?? `Volunteer #${record.volunteer_id}`}
                    </td>
                    <td className="px-4 py-3">
                      {record.training_name ?? "--"}
                    </td>
                    <td className="px-4 py-3">
                      {record.completion_date
                        ? new Date(record.completion_date).toLocaleDateString()
                        : "--"}
                    </td>
                    <td className="px-4 py-3">{record.notes ?? "--"}</td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          if (
                            confirm("Delete this training record?")
                          ) {
                            deleteMutation.mutate(record.id);
                          }
                        }}
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
